import { Suspense, lazy, useEffect, useRef, useState } from "react";

/**
 * Three.js and drei are ~500 KB gzipped. They are pulled in only after the
 * hero is on screen, so a reader arriving on a review page — or on a phone in
 * data-saver mode — never pays for a decoration they will not see.
 */
const FilmReelScene = lazy(() => import("./FilmReel.js"));

function prefersReducedMotion(): boolean {
  return (
    typeof window !== "undefined" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

/**
 * Rules out machines where a WebGL canvas would cost more than it adds.
 *
 * There used to be a `window.innerWidth < 640` test here, and between it and
 * the 1024px one in `canRenderStage` a phone could not get a reel by either
 * route — the flat SVG below was the only thing it was ever shown. Both are
 * gone; what is left is the set of conditions under which a canvas genuinely
 * should not be built.
 *
 * In practice this path is now the fallback's fallback. The home page reaches
 * it only when `canRenderStage` said no, and everything that makes it say no —
 * no WebGL, data saver — makes this say no too, so what renders is the SVG.
 * It is kept because this component does not know who is asking.
 */
function canRenderWebGl(): boolean {
  if (typeof window === "undefined") return false;
  if (prefersReducedMotion()) return false;

  const connection = (
    navigator as Navigator & { connection?: { saveData?: boolean } }
  ).connection;
  if (connection?.saveData) return false;

  try {
    const canvas = document.createElement("canvas");
    return Boolean(
      canvas.getContext("webgl2") ?? canvas.getContext("webgl"),
    );
  } catch {
    return false;
  }
}

/**
 * Static stand-in shown before the canvas loads, and permanently when WebGL is
 * unavailable, motion is reduced, or the viewport is small. Pure SVG, so it
 * costs nothing beyond the markup.
 */
function ReelFallback() {
  return (
    <svg
      viewBox="0 0 200 200"
      className="h-full w-full"
      role="img"
      aria-label="Illustration of a film reel"
    >
      <circle cx="100" cy="100" r="76" fill="#16161b" stroke="#e6a92c" strokeWidth="5" />
      <circle cx="100" cy="100" r="16" fill="#e6a92c" />
      {Array.from({ length: 8 }, (_, index) => {
        const angle = (index / 8) * Math.PI * 2;
        return (
          <circle
            key={index}
            cx={100 + Math.cos(angle) * 46}
            cy={100 + Math.sin(angle) * 46}
            r="15"
            fill="#08080a"
          />
        );
      })}
    </svg>
  );
}

export function HeroCanvas() {
  const container = useRef<HTMLDivElement>(null);
  const [shouldRender, setShouldRender] = useState(false);

  useEffect(() => {
    if (!canRenderWebGl()) return;

    const element = container.current;
    if (!element) return;

    // IntersectionObserver rather than an immediate load: the import only
    // fires once the hero is actually about to be seen.
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setShouldRender(true);
          observer.disconnect();
        }
      },
      { rootMargin: "150px" },
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={container}
      className="relative aspect-square w-full max-w-[26rem]"
    >
      {shouldRender ? (
        <Suspense fallback={<ReelFallback />}>
          <FilmReelScene />
        </Suspense>
      ) : (
        <ReelFallback />
      )}
    </div>
  );
}
